import React, { Children, FC, ReactElement } from "react";
import { TabProps } from "./components/Tab";

import "./Tabs.scss";

export interface TabsProps {
  /**
   * The key of the currently active tab.
   * This key is used to determine which tab and its content should be displayed.
   */
  activeKey: string;
  /**
   * Callback function that is triggered when a tab is selected.
   * It receives the key of the selected tab as an argument.
   */
  onSelect: (key: string) => void;
  /**
   * If true, the tabs will span the full width of their container.
   */
  isFullWidth?: boolean;
  /**
   * The style type of the tabs.
   */
  type?: "button-gray" | "underline" | "button-white-border" | "secondary-gray";
  /**
   * The style of the tabs.
   */
  style?: React.CSSProperties;
  /**
   * The children prop should be an array of `Tab` elements.
   * Each `Tab` should have a corresponding `TabProps` interface.
   * This defines the individual tabs to be rendered.
   */
  children: ReactElement<TabProps>[];
}

const Tabs: FC<TabsProps> = function ({ style, activeKey, onSelect, type = "button-gray", isFullWidth = false, children }) {
  const tabsClass = [`v2-tabs tabs-${type}`, isFullWidth && "tabs-full-width"].filter(Boolean).join(" ");
  return (
    <>
      {/* Render the tabs */}
      <div className={tabsClass} style={style}>
        {Children.map(children, (child) => {
          if (!React.isValidElement(child)) return null;
          const { eventKey, title } = child.props;
          const isActive = eventKey === activeKey;
          return (
            <button
              key={eventKey}
              className={`tab-button ${isActive ? "tab-active" : ""}`}
              onClick={() => onSelect(eventKey)}
              type="button"
            >
              {title}
            </button>
          );
        })}
      </div>
      {/* Render the content of the active tab */}
      {Children.map(children, (child) => {
        if (!React.isValidElement(child)) return null;
        return child.props.eventKey === activeKey ? child.props.children : null;
      })}
    </>
  );
};

Tabs.defaultProps = {
  isFullWidth: false,
  type: "button-gray",
  style: {},
};

export default Tabs;
